// Select an existing, already-uploaded document from the relevant context
// (e.g. the contract's documents) to satisfy a requirement document field —
// the EXT counterpart of the main app's DialogDocumentPicker. Selection-only;
// uploading a brand-new file stays on the field's own Upload button.

// react
import { useEffect, useMemo, useRef, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputAdornment,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Radio,
	TextField,
	Typography,
} from '@mui/material';

// icons
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SearchIcon from '@mui/icons-material/Search';

// types
import { AiDocument } from '../../../@types';

// utils
import { formatDate } from '../../../utils/dates';

export interface PickedDocument {
	id: string;
	filename: string;
}

export interface DialogDocumentPickerProps {
	isOpen: boolean;
	onClose: () => void;
	/** Called with the picked documents when the user confirms. */
	onConfirm: (docs: PickedDocument[]) => void;
	/** The documents to choose from (already-fetched context documents). */
	documents: AiDocument[];
	/** 1 = single-select (radio); >1 or 0 = multi-select (checkbox). Default 1. */
	maxSelections?: number;
	/** Document ids already applied to the field (pre-checked). */
	currentSelections?: string[];
	title?: string;
}

const uploaderName = (doc: AiDocument): string | null => {
	const u = doc.uploaded_by;
	if (u && typeof u === 'object') {
		return (
			u.displayName ||
			[u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
			u.email ||
			null
		);
	}
	return null;
};

export const DialogDocumentPicker = ({
	isOpen,
	onClose,
	onConfirm,
	documents,
	maxSelections = 1,
	currentSelections = [],
	title,
}: DialogDocumentPickerProps) => {
	const { t } = useTranslation();
	const isSingleSelect = maxSelections === 1;

	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(currentSelections));
	const [search, setSearch] = useState('');

	// Seed selection only when the dialog transitions to open — never on a
	// parent re-render, so an in-progress pick is never wiped before the user
	// can confirm.
	const wasOpen = useRef(false);
	useEffect(() => {
		if (isOpen && !wasOpen.current) {
			setSelectedIds(new Set(currentSelections));
			setSearch('');
		}
		wasOpen.current = isOpen;
	}, [isOpen, currentSelections]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return documents;
		return documents.filter((d) => (d.filename || d.file_name || '').toLowerCase().includes(q));
	}, [documents, search]);

	const toggle = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				if (isSingleSelect) next.clear();
				else if (maxSelections > 0 && next.size >= maxSelections) return prev;
				next.add(id);
			}
			return next;
		});
	};

	const confirm = () => {
		const picked: PickedDocument[] = documents
			.filter((d) => selectedIds.has(String(d.id)))
			.map((d) => ({ id: String(d.id), filename: d.filename || d.file_name || `#${d.id}` }));
		onConfirm(picked);
		onClose();
	};

	return (
		<Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{title || t('selectExistingDocument')}</DialogTitle>
			<DialogContent dividers>
				<TextField
					fullWidth
					size="small"
					placeholder={t('search')}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ mb: 1.5 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
					}}
				/>
				{filtered.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
						{t('noDocuments')}
					</Typography>
				) : (
					<List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
						{filtered.map((d) => {
							const id = String(d.id);
							const name = d.filename || d.file_name || `#${id}`;
							const who = uploaderName(d);
							const when = d.uploaded_at ? formatDate({ date: new Date(d.uploaded_at) }) : null;
							const meta = [who, when].filter(Boolean).join(' · ');
							const selected = selectedIds.has(id);
							return (
								<ListItemButton key={id} onClick={() => toggle(id)} selected={selected}>
									<ListItemIcon sx={{ minWidth: 40 }}>
										{isSingleSelect ? (
											<Radio edge="start" checked={selected} tabIndex={-1} disableRipple />
										) : (
											<Checkbox edge="start" checked={selected} tabIndex={-1} disableRipple />
										)}
									</ListItemIcon>
									<InsertDriveFileOutlinedIcon
										sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }}
									/>
									<ListItemText primary={name} secondary={meta || null} />
								</ListItemButton>
							);
						})}
					</List>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{t('cancel')}</Button>
				<Button variant="contained" onClick={confirm} disabled={selectedIds.size === 0}>
					{t('confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
